import React, { useCallback, useEffect, useState } from 'react';
import Footer from '../../Footer';
import axios from 'axios';
import Select from "react-select";
import NavbarHome from '../../Navbar/NavbarHome';
import OutletSearch from '../../SelectSearch/OutletSearch';
import PageStatus from '../../PageStatus/PageStatus';

const ItemReport = () => {
    const today = new Date().toLocaleDateString('en-CA')
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");

    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [error, setError] = useState(null);
    const [itemlist, setItemlist] = useState([]);
    const [selectedItem, setSelectedItem] = useState("");
    const [loading, setLoading] = useState(false);
    const [iData, setIData] = useState([]);
    const [isDisabled, setIsDisabled] = useState(true);
    const [searched, setSearched] = useState(false);
    const [showVoid, setShowVoid] = useState(true);
    const [showNonVoid, setShowNonVoid] = useState(true);

    const fetchItems = useCallback(async () => {
        if (!selectedOutlet || !token) return;
        try {
            setLoading(true);
            setError(null);

            const itemResponse = await axios.post(
                `${baseUrl}/ordertrackeritem-lists`,
                {
                    outlet: selectedOutlet,
                    token,
                }
            );

            const formattedItems = itemResponse.data.items.map((item) => ({
                label: item,
                value: item,
            }));

            setItemlist(formattedItems);
            setIsDisabled(false);
        } catch (err) {
            setError(
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                "Failed to fetch items list"
            );
        } finally {
            setLoading(false);
        }
    }, [selectedOutlet, token, baseUrl]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const handleSearch = async (e) => {
        e.preventDefault();

        if (!selectedOutlet || !token) return;

        if (!selectedItem.trim()) {
            setError("Please select an item");
            return;
        }

        setLoading(true);
        setError(null);
        setSearched(true);

        try {
            const response = await axios.post(
                `${baseUrl}/items-orders-datewise`,
                {
                    startDate: startDate,
                    endDate: endDate,
                    outletName: selectedOutlet,
                    itemName: selectedItem
                }
            );

            const fetchedData = response.data.orders || [];
            // Apply the filter based on checkbox selections
            const filteredData = fetchedData.map(dateGroup => {
                const filteredOrders = dateGroup.orders.map(order => ({
                    ...order,
                    items: order.items.filter(item =>
                        (showVoid && item.completedAt === "VOID") ||
                        (showNonVoid && item.completedAt !== "VOID")
                    )
                })).filter(order => order.items.length > 0);

                return { ...dateGroup, orders: filteredOrders };
            }).filter(dateGroup => dateGroup.orders.length > 0);

            setIData(filteredData);
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || "Failed to fetch item data");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateStr).toLocaleDateString(undefined, options);
    };

    const isCheckDisabled =
        loading || error || !searched || iData.length === 0;

    return (
        <div className='min-h-screen flex flex-col'>
            <NavbarHome />

            <div className='flex-grow p-4'>
                <div className='max-w-6xl mx-auto'>
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <label
                                htmlFor="startDate"
                                className="font-medium text-gray-700 whitespace-nowrap"
                            >
                                Start Date:
                            </label>

                            <input
                                type="date"
                                id="startDate"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                            />
                        </div>

                        <div className="flex justify-center">
                            <OutletSearch
                                selectedOutlet={selectedOutlet}
                                setSelectedOutlet={setSelectedOutlet}
                                token={token}
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <label
                                htmlFor="endDate"
                                className="font-medium text-gray-700 whitespace-nowrap"
                            >
                                End Date:
                            </label>

                            <input
                                type="date"
                                id="endDate"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                            />
                        </div>
                    </div>

                    <div className='flex justify-between items-center mt-3'>
                        <Select
                            options={itemlist}
                            value={itemlist.find(item => item.value === selectedItem) || null}
                            onChange={(option) => setSelectedItem(option?.value || "")}
                            isDisabled={isDisabled}
                            isSearchable
                            placeholder="Search and select an item..."
                            className="w-96"
                            classNamePrefix="react-select"
                        />

                        <button
                            onClick={handleSearch}
                            disabled={loading}
                            className="px-4 py-2 rounded-md font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md text-sm transition"
                        >
                            {loading ? 'Loading...' : 'Search'}
                        </button>

                        <label
                            className={`flex items-center ${isCheckDisabled
                                ? "cursor-not-allowed opacity-50 text-gray-400"
                                : "cursor-pointer"
                                }`}
                        >
                            <input
                                type="checkbox"
                                checked={showVoid}
                                onChange={() => setShowVoid(!showVoid)}
                                className="mr-2 disabled:cursor-not-allowed"
                                disabled={isCheckDisabled}
                            />
                            Show VOID Items
                        </label>

                        <label
                            className={`flex items-center ${isCheckDisabled
                                ? "cursor-not-allowed opacity-50 text-gray-400"
                                : "cursor-pointer"
                                }`}
                        >
                            <input
                                type="checkbox"
                                checked={showNonVoid}
                                onChange={() => setShowNonVoid(!showNonVoid)}
                                className="mr-2 disabled:cursor-not-allowed"
                                disabled={isCheckDisabled}
                            />
                            Show Non-VOID Items
                        </label>
                    </div>

                    <PageStatus
                        loading={loading}
                        error={error}
                        loadingText="Loading data..."
                    />

                    {!loading && !error && searched && iData.length > 0 && (
                        <>
                            <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                                <table className="w-full border-collapse text-sm text-center">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            <th className="px-4 py-3 font-semibold text-gray-700 border-b">
                                                Date
                                            </th>
                                            <th className="px-4 py-3 font-semibold text-gray-700 border-b">
                                                Ordered At
                                            </th>
                                            <th className="px-4 py-3 font-semibold text-gray-700 border-b">
                                                Completed At
                                            </th>
                                            <th className="px-4 py-3 font-semibold text-gray-700 border-b">
                                                Total Time
                                            </th>
                                            <th className="px-4 py-3 font-semibold text-gray-700 border-b">
                                                Avg Prep Time
                                            </th>
                                            <th className="px-4 py-3 font-semibold text-gray-700 border-b">
                                                Quantity
                                            </th>
                                            <th className="px-4 py-3 font-semibold text-gray-700 border-b">
                                                KOT ID
                                            </th>
                                            <th className="px-4 py-3 font-semibold text-gray-700 border-b">
                                                Table No.
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-gray-100">
                                        {iData.map((dateGroup, dateIndex) => {
                                            const filteredOrders = dateGroup.orders
                                                .map(order => ({
                                                    ...order,
                                                    items: order.items.filter(item =>
                                                        (showVoid && item.completedAt === "VOID") ||
                                                        (showNonVoid && item.completedAt !== "VOID")
                                                    ),
                                                }))
                                                .filter(order => order.items.length > 0);

                                            if (filteredOrders.length === 0) return null;

                                            return (
                                                <React.Fragment key={dateIndex}>
                                                    {/* Date Group Header */}
                                                    <tr>
                                                        <td
                                                            colSpan={8}
                                                            className="bg-violet-50 text-left px-4 py-2.5 font-semibold text-violet-700 border-y"
                                                        >
                                                            {formatDate(dateGroup.Date)}
                                                        </td>
                                                    </tr>

                                                    {filteredOrders.map((order, orderIndex) => (
                                                        <React.Fragment key={orderIndex}>
                                                            {order.items.map((item, itemIndex) => {
                                                                const isVoid = item.completedAt === "VOID";

                                                                return (
                                                                    <tr
                                                                        key={itemIndex}
                                                                        className={`hover:bg-gray-50 transition-colors ${isVoid ? "bg-red-50" : ""
                                                                            }`}
                                                                    >
                                                                        <td className="px-4 py-2.5"></td>

                                                                        <td
                                                                            className={`px-4 py-2.5 ${isVoid
                                                                                ? "text-red-600 font-medium"
                                                                                : "text-gray-700"
                                                                                }`}
                                                                        >
                                                                            {item.orderedAt}
                                                                        </td>

                                                                        <td
                                                                            className={`px-4 py-2.5 ${isVoid
                                                                                ? "text-red-600 font-medium"
                                                                                : "text-gray-700"
                                                                                }`}
                                                                        >
                                                                            {item.completedAt}
                                                                        </td>

                                                                        <td className="px-4 py-2.5 text-gray-700">
                                                                            {item.TotalTime}
                                                                        </td>

                                                                        <td className="px-4 py-2.5 text-gray-700">
                                                                            {item.AvgPrepTime === "00:00:00"
                                                                                ? "-"
                                                                                : item.AvgPrepTime}
                                                                        </td>

                                                                        <td className="px-4 py-2.5 text-center">
                                                                            {item.Quantity}
                                                                        </td>

                                                                        <td className="px-4 py-2.5 font-medium text-gray-800">
                                                                            {order.KOTID}
                                                                        </td>

                                                                        <td className="px-4 py-2.5">
                                                                            {order.TableNum}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </React.Fragment>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ItemReport;
